import { getHallOfFame } from './hallOfFameApi';
import { getCrownLabel, getCrownTitle } from './avatarCrowns';

// Hall of Fame data drives the "top player per avatar" listing as well
// as the crown badges shown on player profiles. Both surfaces want the
// same snapshot, and HoF only changes when a match resolves, so we
// keep a process-level cache with a short TTL and dedupe concurrent
// requests. Profile popovers that open while a fetch is already in
// flight reuse it instead of firing a parallel request.
const TTL_MS = 2 * 60 * 1000;

let cached = null;
let cachedAt = 0;
let inFlight = null;

export async function getHallOfFameCached() {
  const now = Date.now();
  if (cached && now - cachedAt < TTL_MS) return cached;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const data = await getHallOfFame();
      cached = data;
      cachedAt = Date.now();
      return data;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export function invalidateHallOfFameCache() {
  cached = null;
  cachedAt = 0;
}

// Crowns held by every player in a Hall of Fame snapshot, keyed by
// player id. Returns rich objects (image, elements, title, label) so the
// UI can render without consulting the catalog again. Each list is
// sorted by wins desc so a displayed title defaults to the player's
// highest-played crown.
export function computeCrownsByPlayer(hofData, sorceryCards) {
  const byPlayer = new Map();
  if (!hofData?.avatars) return byPlayer;
  const catalogByName = new Map();
  const catalogById = new Map();
  if (Array.isArray(sorceryCards)) {
    for (const c of sorceryCards) {
      if (c.type !== 'Avatar') continue;
      const meta = {
        id: c.unique_id,
        name: c.name || c.unique_id,
        image: c.printings?.[0]?.image_url || null,
        elements: Array.isArray(c.elements)
          ? c.elements.map((e) => e?.name).filter(Boolean)
          : [],
      };
      catalogByName.set(meta.name, meta);
      catalogById.set(meta.id, meta);
    }
  }

  for (const row of hofData.avatars) {
    const holderId = row?.topPlayer?.id;
    if (!holderId) continue;
    const meta = catalogById.get(row.avatarId) || { id: row.avatarId, name: row.avatarId, image: null, elements: [] };
    const crowns = byPlayer.get(holderId) || [];
    crowns.push({
      avatarId: row.avatarId,
      avatarName: meta.name,
      image: meta.image,
      elements: meta.elements,
      wins: row.topPlayer.wins || 0,
      title: getCrownTitle(meta.name),
      label: getCrownLabel(meta.name),
    });
    byPlayer.set(holderId, crowns);
  }
  for (const crowns of byPlayer.values()) {
    crowns.sort((a, b) => b.wins - a.wins || a.avatarName.localeCompare(b.avatarName));
  }
  return byPlayer;
}

// Memoised crowns-by-player index. Building it walks the whole card
// catalog, which list surfaces that resolve crowns for every visible row
// (the leaderboards) would otherwise repeat per row. Keyed on the
// identities of the snapshot and catalog it was built from, so a new
// Hall of Fame fetch or a catalog reload rebuilds it.
let crownIndex = null;
let crownIndexHof = null;
let crownIndexCards = null;

function getCrownIndex(hofData, sorceryCards) {
  if (crownIndex && crownIndexHof === hofData && crownIndexCards === sorceryCards) return crownIndex;
  crownIndex = computeCrownsByPlayer(hofData, sorceryCards);
  crownIndexHof = hofData;
  crownIndexCards = sorceryCards;
  return crownIndex;
}

// Crowns held by a single player. The returned array is shared with the
// index. Treat it as read-only.
export function computeCrownsForUser(hofData, userId, sorceryCards) {
  if (!userId) return [];
  return getCrownIndex(hofData, sorceryCards).get(userId) || [];
}

export async function getCrownsForUser(userId, sorceryCards) {
  if (!userId) return [];
  const data = await getHallOfFameCached();
  return computeCrownsForUser(data, userId, sorceryCards);
}
